// National base-reflectivity mosaic from the Iowa Environmental Mesonet
// WMS-T archive. One 5-minute CONUS composite per timestamp, from 16 Feb 2011.
// https://mesonet.agron.iastate.edu/cgi-bin/wms/nexrad/n0q-t.cgi

export const IEM_MOSAIC_WMS = "https://mesonet.agron.iastate.edu/cgi-bin/wms/nexrad/n0q-t.cgi";
export const IEM_MOSAIC_LAYER = "nexrad-n0q-wmst";
export const MOSAIC_PANE = "mosaicPane";
export const MOSAIC_STEP_MS = 5 * 60 * 1000;
export const MOSAIC_SLOTS = (24 * 60 * 60 * 1000) / MOSAIC_STEP_MS;
export const MOSAIC_ARCHIVE_START_MS = Date.UTC(2011, 1, 16);
export const MOSAIC_LIVE_LAG_MS = 20 * 60 * 1000;
export const MOSAIC_CONUS_BOUNDS = [[24, -125], [50, -66]];

export function snapMosaicMs(ms) {
  if (!Number.isFinite(ms)) return NaN;
  return Math.floor(ms / MOSAIC_STEP_MS) * MOSAIC_STEP_MS;
}

export function mosaicArchiveCovers(ms) {
  const snapped = snapMosaicMs(ms);
  return Number.isFinite(snapped) && snapped >= MOSAIC_ARCHIVE_START_MS;
}

export function formatMosaicTimeParam(ms) {
  const d = new Date(snapMosaicMs(ms));
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:00Z`;
}

export function formatMosaicClock(ms) {
  const iso = formatMosaicTimeParam(ms);
  return `${iso.slice(11, 16)} UTC`;
}

export function formatMosaicDateCompact(ms) {
  return formatMosaicTimeParam(ms).slice(0, 10).replace(/-/g, "");
}

export function formatMosaicTimeCompact(ms) {
  const iso = formatMosaicTimeParam(ms);
  return `${iso.slice(11, 13)}${iso.slice(14, 16)}00`;
}

export function mosaicSlotIndex(ms) {
  const snapped = snapMosaicMs(ms);
  const d = new Date(snapped);
  const midnight = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.max(0, Math.min(MOSAIC_SLOTS - 1, Math.round((snapped - midnight) / MOSAIC_STEP_MS)));
}

export function mosaicMsFromSlot(dateStr, slot) {
  const [y, m, d] = String(dateStr || "").split("-").map(Number);
  if (!y || !m || !d) return NaN;
  const midnight = Date.UTC(y, m - 1, d);
  const bounded = Math.max(0, Math.min(MOSAIC_SLOTS - 1, slot | 0));
  return midnight + bounded * MOSAIC_STEP_MS;
}

export function createMosaicLayer(L, map, { opacity = 0.85, timeMs } = {}) {
  if (!map.getPane(MOSAIC_PANE)) {
    const pane = map.createPane(MOSAIC_PANE);
    // Above the satellite pane (350) so the mosaic draws over GOES imagery.
    pane.style.zIndex = "360";
    pane.style.pointerEvents = "none";
  }
  const layer = L.tileLayer.wms(IEM_MOSAIC_WMS, {
    layers: IEM_MOSAIC_LAYER,
    format: "image/png",
    transparent: true,
    version: "1.1.1",
    time: formatMosaicTimeParam(timeMs),
    opacity,
    pane: MOSAIC_PANE,
    attribution: "NEXRAD composite © Iowa Environmental Mesonet",
  });
  layer.setMosaicTime = function setMosaicTime(ms) {
    this.setParams({ time: formatMosaicTimeParam(ms) });
    return this;
  };
  return layer;
}
